import React, { useMemo } from 'react'
import { ReactComponent as User } from '@mike/mike-shared-frontend/media/icons/User'
import { ReactComponent as Users } from '@mike/mike-shared-frontend/media/icons/Users'
import { Tabs } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { LinkTab, ActiveLabel, activeSvgStyle } from '../AppNav'
import AppBreadcrumbNav from '../AppBreadcrumpsNav'

const StyledBreadcrumb = styled('div')(({ theme }) => ({
  paddingLeft: '30px',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1)
}))

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid' + theme.palette.grey[300],
  paddingLeft: '30px',
  '& .MuiTabs-indicator': {
    height: 3
  }
}))

interface IProps {
  projectId: string
}

const ProjectAccessNav = ({ projectId }: IProps) => {
  const intl = useIntl()
  const currentPath = useLocation().pathname

  const base = currentPath.startsWith('/site/') ? 'site' : 'project'

  const showUsersTab = base === 'site'

  // Tabs are keyed by name rather than index so nothing depends on how many are
  // rendered; `false` is MUI's "no tab selected", used for any other path under
  // /access so the indicator isn't parked on an unrelated tab.
  const activeTab = useMemo(() => {
    if (currentPath.endsWith('/access/api')) return 'api'
    if (currentPath.endsWith('/access/users')) return 'users'
    return false
  }, [currentPath])

  const usersLabel = intl.formatMessage({
    id: 'projectAccess.nav.users',
    defaultMessage: 'Users'
  })
  const apiLabel = intl.formatMessage({
    id: 'projectAccess.nav.api',
    defaultMessage: 'API access'
  })

  return (
    <>
      <StyledBreadcrumb>
        <AppBreadcrumbNav allowLastLink />
      </StyledBreadcrumb>
      <StyledTabs
        value={activeTab}
        indicatorColor="secondary"
        textColor="primary"
      >
        {showUsersTab && (
          <LinkTab
            value="users"
            to={`/${base}/${projectId}/access/users`}
            label={
              activeTab === 'users' ? (
                <ActiveLabel>{usersLabel}</ActiveLabel>
              ) : (
                <span>{usersLabel}</span>
              )
            }
            icon={
              <Users
                style={activeTab === 'users' ? activeSvgStyle : undefined}
              />
            }
          />
        )}
        <LinkTab
          value="api"
          to={`/${base}/${projectId}/access/api`}
          label={
            activeTab === 'api' ? (
              <ActiveLabel>{apiLabel}</ActiveLabel>
            ) : (
              <span>{apiLabel}</span>
            )
          }
          icon={
            <User style={activeTab === 'api' ? activeSvgStyle : undefined} />
          }
        />
      </StyledTabs>
    </>
  )
}

export default ProjectAccessNav
